import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import Stepper from '@material-ui/core/Stepper';
import Step from '@material-ui/core/Step';
import StepButton from '@material-ui/core/StepButton';
import StepLabel from '@material-ui/core/StepLabel';
import StepContent from '@material-ui/core/StepContent';
import Button from '@material-ui/core/Button';
import Hidden from '@material-ui/core/Hidden';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';

import ORContentHeader from 'components/ORContentHeader/ORContentHeader';
import ORTypography from 'components/ORTypography/ORTypography';
import LoadingPlaceholder from 'components/LoadingPlaceholder/LoadingPlaceholder';
import BillingPageContainer from 'components/BillingPage/BillingPageContainer';
import BusyButton from 'components/BusyButton/BusyButton';
import SummaryDrawer from './SummaryDrawer/SummaryDrawerContainer';
import StepOneContainer from './StepOne/StepOneContainer';
import StepTwoContainer from './StepTwo/StepTwoContainer';
import StepThreeContainer from './StepThree/StepThreeContainer';
import StepFour from './StepFour/StepFour';

const propTypes = {
  catalogIsLoading: PropTypes.bool.isRequired,
  createIsLoading: PropTypes.bool.isRequired,
  stepOneComplete: PropTypes.bool.isRequired,
  stepTwoComplete: PropTypes.bool.isRequired,
  stepThreeComplete: PropTypes.bool.isRequired,
  stepFourComplete: PropTypes.bool.isRequired,
  createInstanceBody: PropTypes.shape({
    service: PropTypes.string,
    version: PropTypes.string,
    region: PropTypes.string,
    provider: PropTypes.string,
    serviceSubtype: PropTypes.string,
    features: PropTypes.shape({}),
    plan: PropTypes.string,
    name: PropTypes.string,
  }),
  mongoLicenseWarningNeeded: PropTypes.bool.isRequired,
  needsBilling: PropTypes.bool.isRequired,
  createInstance: PropTypes.func.isRequired,
  getIp: PropTypes.func.isRequired,
  destroy: PropTypes.func.isRequired,
  trackButtonClickSet: PropTypes.func.isRequired,
};

const defaultProps = {
  createInstanceBody: {
    service: '',
  },
};

const useStyles = makeStyles((theme) => ({
  createContainer: {
    height: '100%',
  },
  stepperContainer: {
    height: '100%',
    backgroundColor: theme.customColors.white,
  },
  headerContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    borderBottom: `1px solid ${theme.customColors.lightGrey}`,
  },
  header: {
    color: theme.customColors.black,
    fontSize: '1.2857rem',
  },
  stepper: {
    maxWidth: 700,
    padding: theme.spacing(1),
  },
  stepButtonIcon: {
    color: [theme.customColors.teal, '!important'],
  },
  theArrow: {
    color: theme.customColors.orange,
    marginRight: theme.spacing(1),
  },
  summaryButton: {
    color: theme.customColors.teal,
    textTransform: 'uppercase',
    fontSize: '1.2857rem',
    cursor: 'pointer',
  },
  stepActionButton: {
    width: '100%',
    maxWidth: '15rem',
    marginRight: theme.spacing(2),
    display: 'inline-flex',

    [theme.breakpoints.down('sm')]: {
      maxWidth: 'unset',
      margin: 'unset',
      '&:nth-child(2)': {
        marginLeft: theme.spacing(1),
      },
    },
  },
  busyButtonWrapperAndButton: {
    width: '100%',
  },
  monthlyCost: {
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
  estimate: {
    [theme.breakpoints.down('xs')]: {
      display: 'none',
    },
  },
  mobileStepperContainer: {
    border: `1px solid ${theme.customColors.lightGrey}`,
    marginTop: theme.spacing(1),
    padding: theme.spacing(1),
    borderRadius: 3,
  },
  mobileHeaderContainer: {
    marginBottom: theme.spacing(1),
  },
  mobileStepper: {
    display: 'flex',
    padding: `${theme.spacing(4)}px 0 ${theme.spacing(1)}px 0`,
    backgroundColor: theme.customColors.white,
  },
  mobileProgressBar: {
    display: 'none',
  },
}));

function getSteps() {
  return [
    'Service, Region, and Provider',
    'Configure and Customize',
    'Authorized Firewall IPs',
    'Name Your Instance',
  ];
}

export function getStepContent(step) {
  switch (step) {
    case 0:
      return <StepOneContainer />;
    case 1:
      return <StepTwoContainer />;
    case 2:
      return <StepThreeContainer />;
    case 3:
      return <StepFour />;
    default:
      return null;
  }
}

export function UnstyledCreateInstancePage({
  catalogIsLoading,
  createIsLoading,
  stepOneComplete,
  stepTwoComplete,
  stepThreeComplete,
  stepFourComplete,
  createInstanceBody,
  createInstance,
  mongoLicenseWarningNeeded,
  needsBilling,
  getIp,
  destroy,
  history,
  trackButtonClickSet,
}) {
  React.useEffect(() => {
    getIp();
  }, [getIp]);

  const stepsComplete = [stepOneComplete, stepTwoComplete, stepThreeComplete, stepFourComplete];
  const [activeStep, setActiveStep] = React.useState(0);
  const [mongoWarningOpen, setMongoWarningOpen] = React.useState(false);

  // Above a small desktop width, have the drawer open on initial loads. On smaller
  // screens, the drawer is closed on initial load.
  const [summaryOpen, setSummaryOpen] = React.useState(window.innerWidth > 1024);
  const [billingOpen, setBillingOpen] = React.useState(false);

  const classes = useStyles();

  const steps = getSteps();
  const isLastStep = activeStep === steps.length - 1;

  const trackStep = (index) => {
    const stepsArray = getSteps();
    const nextStep = stepsArray[index];
    trackButtonClickSet(nextStep);
  };

  const handleStep = (step) => () => {
    setActiveStep(step);
    trackStep(step);
  };
  const handlePrevious = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
    trackStep(activeStep - 1);
  };
  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
    trackStep(activeStep + 1);
  };
  const toggleSummary = () => {
    setSummaryOpen((prevSummaryOpen) => {
      const newSummaryOpen = !prevSummaryOpen;
      trackButtonClickSet(`Summary Toggled ${newSummaryOpen ? '(Open)' : '(Closed)'}`);
      return newSummaryOpen;
    });
  };
  const handleReset = () => {
    setActiveStep(() => {
      destroy('create');
      trackButtonClickSet('Cancel');
      return 0;
    });
  };

  const contactSupportClick = () => {
    setMongoWarningOpen(true);
  };
  const supportAlertOKClick = () => {
    destroy('create');
    setMongoWarningOpen(false);
  };
  const handleSupportAlertClose = () => {
    setMongoWarningOpen(false);
  };

  const redirectToInstances = () => history.push('/mission-ctrl/instances');

  const createInstanceClick = () => {
    trackButtonClickSet('Create Instance Submit');
    if (needsBilling) {
      const buttonName = 'Instance Snapshot on Billing Popup';
      const data = {
        analytics: {
          eventPayload: {
            event: buttonName,
            properties: {
              instance_name: createInstanceBody.name,
              service: createInstanceBody.service,
              plan: createInstanceBody.plan,
              version: createInstanceBody.version,
              region: createInstanceBody.region,
              provider: createInstanceBody.provider,
              service_subtype: createInstanceBody.serviceSubtype,
              features: createInstanceBody.features,
            },
          },
        },
      };
      trackButtonClickSet(buttonName, data);
      setBillingOpen(true);
    } else {
      createInstance(createInstanceBody, redirectToInstances);
    }
  };

  let advanceButtonText = 'Continue';
  let advanceButtonAction = handleNext;
  if (mongoLicenseWarningNeeded) {
    advanceButtonText = 'Contact Support';
    advanceButtonAction = contactSupportClick;
  } else if (isLastStep) {
    advanceButtonText = 'Create Instance';
    advanceButtonAction = createInstanceClick;
  }

  return (
    <React.Fragment>
      <Dialog open={mongoWarningOpen} onClose={handleSupportAlertClose}>
        <DialogContent>
          MongoDB in AWS/GCP/Azure is currently only available in private DBaaS regions. For
          information and pricing on private DBaaS deployments please contact{' '}
          <a href="mailto:support@objectrocket.com">support@objectrocket.com</a>, or see{' '}
          <a
            href="https://www.objectrocket.com/private-dbaas/"
            target="_blank"
            rel="noopener noreferrer"
          >
            our docs
          </a>
          .
        </DialogContent>
        <DialogActions>
          <Button color="primary" onClick={supportAlertOKClick}>
            OK
          </Button>
        </DialogActions>
      </Dialog>
      {catalogIsLoading ? (
        <LoadingPlaceholder />
      ) : (
        <div className={classes.createContainer}>
          <div className={classes.headerContainer}>
            <ORContentHeader>Create an Instance</ORContentHeader>
            <ORTypography
              variant="h5"
              component="h1"
              className={classes.summaryButton}
              onClick={toggleSummary}
            >
              <span className={classes.theArrow}>&lt;</span>
              <span className={classes.monthlyCost}>Monthly Cost </span>
              <span className={classes.estimate}>Estimate</span>
            </ORTypography>
          </div>

          <div className={classes.stepperContainer}>
            <Hidden mdUp>
              <div className={classes.mobileStepperContainer}>
                <div className={classes.mobileHeaderContainer}>
                  <ORTypography variant="h5" component="h2" className={classes.header}>
                    Step {activeStep + 1} of {steps.length}
                  </ORTypography>
                  <ORTypography variant="h5" component="h2" className={classes.header}>
                    {steps[activeStep]}
                  </ORTypography>
                </div>
                {getStepContent(activeStep)}
                <div className={classes.mobileStepper}>
                  {activeStep > 0 && (
                    <Button
                      className={classes.stepActionButton}
                      variant="contained"
                      color="secondary"
                      onClick={handlePrevious}
                    >
                      Previous
                    </Button>
                  )}
                  <BusyButton
                    loading={createIsLoading}
                    className={classes.stepActionButton}
                    classes={{ wrapper: classes.busyButtonWrapperAndButton }}
                    btnClassName={classes.busyButtonWrapperAndButton}
                    disabled={!stepsComplete[activeStep]}
                    mini={false}
                    variant="contained"
                    color="primary"
                    onClick={advanceButtonAction}
                  >
                    {advanceButtonText}
                  </BusyButton>
                </div>
              </div>
            </Hidden>

            <Hidden smDown>
              <Stepper activeStep={activeStep} orientation="vertical" className={classes.stepper}>
                {steps.map((label, index) => (
                  <Step key={label}>
                    <StepButton onClick={handleStep(index)} completed={stepsComplete[index]}>
                      <StepLabel StepIconProps={{ classes: { root: classes.stepButtonIcon } }}>
                        {label}
                      </StepLabel>
                    </StepButton>
                    <StepContent>
                      {getStepContent(index)}
                      <br />
                      {isLastStep && (
                        <Button
                          className={classes.stepActionButton}
                          variant="contained"
                          color="secondary"
                          onClick={handleReset}
                        >
                          Cancel
                        </Button>
                      )}
                      <BusyButton
                        loading={createIsLoading}
                        className={classes.stepActionButton}
                        classes={{ wrapper: classes.busyButtonWrapperAndButton }}
                        btnClassName={classes.busyButtonWrapperAndButton}
                        disabled={!stepsComplete[activeStep]}
                        mini={false}
                        variant="contained"
                        color="primary"
                        onClick={advanceButtonAction}
                      >
                        {advanceButtonText}
                      </BusyButton>
                    </StepContent>
                  </Step>
                ))}
              </Stepper>
            </Hidden>
          </div>
          <SummaryDrawer isOpen={summaryOpen} toggle={toggleSummary} />
        </div>
      )}
      <BillingPageContainer
        open={billingOpen}
        billingOpen={() => setBillingOpen(false)}
        createInstance={createInstance}
        newInstanceData={createInstanceBody}
        redirectToInstances={redirectToInstances}
      />
    </React.Fragment>
  );
}

UnstyledCreateInstancePage.propTypes = propTypes;
UnstyledCreateInstancePage.defaultProps = defaultProps;

export default UnstyledCreateInstancePage;
